import {
  type Dispatch,
  type SetStateAction,
  type FC,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FlowLoadErrors,
  GenericOption,
  RunSmartConnectorFlowLoad,
  SmartConnector,
} from 'pages/SmartConnectors/types';
import { StyledDiv, StyledScopeCard, StyledScopeWrapper } from '../../styles';
import IconButton from '__components/Kizen/IconButton';
import Icon from '__components/Kizen/Icon';
import { IconSizing } from '__components/Kizen/Icon';
import Select from '__components/Inputs/Select';
import { VariableNameInput } from 'pages/SmartConnectors/SmartConnector/VariableNameInput';
import {
  getUniqueName,
  getVariableNameError,
  isCSVLoadStep,
} from 'pages/SmartConnectors/SmartConnector/helpers';
import { FlowBuilderContext } from '../../../context';
import { useFlashTransition } from 'hooks/useFlashState';

const buttonColor = {
  default: 'var(--color-background-dark)',
  hover: 'var(--color-background-darker)',
};

const errorsOrder: Partial<
  Record<keyof RunSmartConnectorFlowLoad['return_scopes'][number], number>
> = {
  source: 0,
  name: 1,
};

export const ScopeSettings: FC<{
  loadId?: string;
  smartConnectorToRun: SmartConnector;
  source: string;
  name: string;
  index: number;
  onChange: Dispatch<SetStateAction<RunSmartConnectorFlowLoad>>;
  showError: boolean;
  errors: FlowLoadErrors['return_scopes'];
  resetError: (
    patch:
      | Partial<FlowLoadErrors>
      | ((prev: FlowLoadErrors) => Partial<FlowLoadErrors>)
  ) => void;
}> = ({
  loadId,
  smartConnectorToRun,
  source,
  name,
  index,
  onChange,
  showError = false,
  errors,
  resetError,
}) => {
  const { t } = useTranslation();

  const { smartConnector } = useContext(FlowBuilderContext);

  const onDeleteScope = () => {
    onChange((prev) => {
      const nextReturnScopes = (prev.return_scopes || []).filter(
        (_, i) => i !== index
      );
      resetError((prev) => {
        return {
          ...prev,
          return_scopes: prev.return_scopes?.filter((_, i) => i !== index),
        };
      });
      return {
        ...prev,
        return_scopes: nextReturnScopes,
      };
    });
  };

  const onChangeSource = useCallback(
    ({ value }: GenericOption) => {
      onChange((prev) => {
        const nextReturnScopes = (prev.return_scopes || []).map((scope, i) => {
          if (i === index) {
            return {
              ...scope,
              source: value,
            };
          }
          return scope;
        });
        resetError((prev) => {
          return {
            ...prev,
            return_scopes: prev.return_scopes?.map((error, i) => {
              if (index === i) {
                return {
                  ...error,
                  source: undefined,
                };
              }
              return error;
            }),
          };
        });
        return {
          ...prev,
          return_scopes: nextReturnScopes,
        };
      });
    },
    [index, onChange, resetError]
  );

  const otherScopes = useMemo(() => {
    const scopes = Object.keys(smartConnector.headers || {});

    scopes.push(
      ...(smartConnector.flow.loads || []).reduce<string[]>((acc, load) => {
        if (
          isCSVLoadStep(load) ||
          !load.return_scopes?.length ||
          (loadId && load.id === loadId)
        ) {
          return acc;
        }
        acc.push(...load.return_scopes.map((returnScope) => returnScope.name));
        return acc;
      }, [])
    );
    return scopes;
  }, [smartConnector, loadId]);

  const onChangeName = (value: string) => {
    let nextValue = value;
    onChange((prev) => {
      nextValue = getUniqueName(
        value,
        (prev.return_scopes || []).reduce<string[]>((acc, scope, i) => {
          if (i !== index && scope.name) {
            acc.push(scope.name);
          }
          return acc;
        }, otherScopes)
      );

      const nameError = getVariableNameError(nextValue, t);

      resetError((prev) => {
        const prevReturnScopes = [...(prev.return_scopes || [])];
        prevReturnScopes[index] = {
          ...prevReturnScopes[index],
          name: nameError ? [nameError] : undefined,
        };
        return {
          ...prev,
          return_scopes: prevReturnScopes,
        };
      });

      return {
        ...prev,
        return_scopes: prev.return_scopes?.map((scope, i) => {
          if (index === i) {
            return {
              ...scope,
              name: nextValue,
            };
          }
          return scope;
        }),
      };
    });

    return nextValue;
  };

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const scopeError = errors?.[index];

  const [isFirstErrorIndex, firstErrorKey] = useMemo(() => {
    const isFirstErrorIndex =
      !!scopeError &&
      errors?.findIndex(
        (err) => Object.values(err || {}).filter(Boolean).length
      ) === index;
    return [
      isFirstErrorIndex,
      (
        Object.keys(scopeError || {}) as (keyof Exclude<
          FlowLoadErrors['return_scopes'],
          undefined
        >[number])[]
      )
        .sort((a, b) => (errorsOrder[a] || 0) - (errorsOrder[b] || 0))
        .find((key) => scopeError?.[key]),
    ];
  }, [scopeError, errors, index]);

  const errorToShow =
    showError && isFirstErrorIndex && firstErrorKey
      ? scopeError?.[firstErrorKey] || null
      : null;

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errorToShow) {
      flashErrorMessage(errorToShow);
      wrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
    return () => cancelFlash();
  }, [errorToShow, flashErrorMessage, cancelFlash]);

  const validate = useMemo(
    () => ({
      message,
      showMessage,
    }),
    [message, showMessage]
  );

  return (
    <StyledScopeWrapper ref={wrapperRef}>
      <IconButton
        title={t('Delete')}
        sizing="dense"
        color={buttonColor}
        onClick={onDeleteScope}
      >
        <IconSizing size="15px">
          <Icon icon="trash" />
        </IconSizing>
      </IconButton>
      <StyledScopeCard hasError={!!firstErrorKey}>
        <StyledDiv width={180}>
          <Select
            value={source}
            options={smartConnectorToRun.returnable_sources?.map((source) => ({
              value: source,
              label: source,
            }))}
            label={t('Child Scope / Load Step')}
            placeholder={t('Choose Scope')}
            onChange={onChangeSource}
            error={!!scopeError?.source}
            validate={firstErrorKey === 'source' ? validate : undefined}
          />
        </StyledDiv>
        <StyledDiv width={180}>
          <VariableNameInput
            value={name}
            label={t('Save as Scope Name')}
            onChange={onChangeName}
            error={!!scopeError?.name}
            validate={firstErrorKey === 'name' ? validate : undefined}
          />
        </StyledDiv>
      </StyledScopeCard>
    </StyledScopeWrapper>
  );
};
